/**
 * Usercentrics Consent Store
 *
 * Global store + actions for consent management without a provider wrapper.
 * Safe for SSR and usable in components or utilities.
 *
 * Domain-specific sync (embeds, tracking) is decoupled via the subscriber
 * pattern: call `onConsentChange(handler)` to react to consent updates.
 */

import { createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { logger } from "~/lib/logger/logger";
import type { ConsentDetails, ServiceConsent, CategoryConsent } from "~/types/consent";

/** How long to wait for Usercentrics to initialize before giving up */
const UC_INIT_TIMEOUT_MS = 10_000;

// --- Store State ---

const [isInitialized, setIsInitialized] = createSignal(false);
const [consentDetails, setConsentDetails] = createSignal<ConsentDetails>();
const [needsRefresh, setNeedsRefresh] = createSignal(false);

export const consentStore = {
  consentDetails,
  needsRefresh,
};

// --- Consent Change Subscribers ---

type ConsentChangeHandler = (details: ConsentDetails) => void;
const changeHandlers: ConsentChangeHandler[] = [];

/**
 * Register a handler that is called whenever consent details change.
 * Returns an unsubscribe function.
 */
export function onConsentChange(handler: ConsentChangeHandler): () => void {
  changeHandlers.push(handler);
  return () => {
    const index = changeHandlers.indexOf(handler);
    if (index >= 0) changeHandlers.splice(index, 1);
  };
}

function notifySubscribers(details: ConsentDetails): void {
  for (const handler of changeHandlers) {
    try {
      handler(details);
    } catch (error) {
      logger.error("[Consent] Subscriber threw:", error);
    }
  }
}

// --- Initialization / Event Handling ---

let listenersBound = false;
const pendingReadyCallbacks: Array<() => void> = [];
const activeTimeouts = new Set<ReturnType<typeof setTimeout>>();

const flushReadyCallbacks = () => {
  if (!isInitialized()) return;
  const callbacks = pendingReadyCallbacks.splice(0, pendingReadyCallbacks.length);
  for (const callback of callbacks) {
    try {
      callback();
    } catch (error) {
      logger.error("[Consent] Ready callback threw:", error);
    }
  }
};

const handleInitialized = () => {
  logger.consent("Usercentrics initialized");
  setIsInitialized(true);
  flushReadyCallbacks();
};

const handleConsentChange = (event: Event) => {
  const detail = (event as CustomEvent<ConsentDetails>).detail;
  logger.consent("Consent changed", {
    status: detail.consent.status,
    updatedBy: detail.consent.updatedBy,
  });
  setConsentDetails(detail);
  notifySubscribers(detail);
};

const loadInitialConsentDetails = async () => {
  if (!window.__ucCmp) return;

  try {
    const initialized = await window.__ucCmp.isInitialized();
    if (!initialized) return;

    logger.consent("Usercentrics already initialized");
    setIsInitialized(true);
    flushReadyCallbacks();

    const details = await window.__ucCmp.getConsentDetails();
    logger.consent("Initial consent details loaded", {
      status: details.consent.status,
    });
    setConsentDetails(details);
    notifySubscribers(details);
  } catch (error) {
    logger.error("Failed to load initial consent details", error);
  }
};

export const initConsentStore = () => {
  if (isServer || listenersBound) return;
  listenersBound = true;

  window.addEventListener("UC_UI_INITIALIZED", handleInitialized);
  window.addEventListener("UC_CONSENT", handleConsentChange);

  loadInitialConsentDetails();
};

export const disposeConsentStore = () => {
  if (isServer || !listenersBound) return;
  activeTimeouts.forEach((t) => clearTimeout(t));
  activeTimeouts.clear();
  window.removeEventListener("UC_UI_INITIALIZED", handleInitialized);
  window.removeEventListener("UC_CONSENT", handleConsentChange);
  listenersBound = false;
  pendingReadyCallbacks.splice(0, pendingReadyCallbacks.length);
  changeHandlers.splice(0, changeHandlers.length);
  setIsInitialized(false);
};

const whenReady = async <T>(callback: () => Promise<T> | undefined): Promise<T | undefined> => {
  if (isServer) return undefined;
  initConsentStore();

  if (isInitialized()) {
    return callback();
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      activeTimeouts.delete(timeout);
      logger.warn("Usercentrics initialization timed out");
      resolve(undefined);
    }, UC_INIT_TIMEOUT_MS);
    activeTimeouts.add(timeout);

    pendingReadyCallbacks.push(() => {
      activeTimeouts.delete(timeout);
      clearTimeout(timeout);
      resolve(callback());
    });
  });
};

// --- Actions ---

export const consentActions = {
  showConsentBanner: () => {
    logger.consent("Opening consent banner");
    return whenReady(() => window.__ucCmp?.showFirstLayer()) as Promise<void>;
  },
  showConsentSettings: () => {
    logger.consent("Opening consent settings");
    return whenReady(() => window.__ucCmp?.showSecondLayer()) as Promise<void>;
  },
  acceptAll: () => {
    logger.consent("Accepting all consents");
    return whenReady(() => window.__ucCmp?.acceptAllConsents()) as Promise<void>;
  },
  denyAll: () => {
    logger.consent("Denying all consents");
    return whenReady(() => window.__ucCmp?.denyAllConsents()) as Promise<void>;
  },
  updateServices: (consents: ServiceConsent[]) => {
    logger.consent("Updating service consents", { count: consents.length });
    return whenReady(() => window.__ucCmp?.updateServicesConsents(consents)) as Promise<void>;
  },
  updateCategories: (consents: CategoryConsent[]) => {
    logger.consent("Updating category consents", { count: consents.length });
    return whenReady(() => window.__ucCmp?.updateCategoriesConsents(consents)) as Promise<void>;
  },
  saveConsents: () => {
    logger.consent("Saving consents");
    return whenReady(() => window.__ucCmp?.saveConsents()) as Promise<void>;
  },
  refreshScripts: () => {
    logger.consent("Refreshing scripts");
    return whenReady(() => window.__ucCmp?.refreshScripts()) as Promise<void>;
  },
  markNeedsRefresh: () => {
    logger.consent("Marking page as needing refresh");
    setNeedsRefresh(true);
  },
  resetNeedsRefresh: () => {
    logger.consent("Resetting needs refresh flag");
    setNeedsRefresh(false);
  },
  refreshPage: () => {
    if (!isServer) {
      logger.consent("Refreshing page");
      window.location.reload();
    }
  },
};
